import React, { useMemo } from 'react';

import { fg } from '@atlaskit/platform-feature-flags/fg';
import UFOLoadHold from '@atlaskit/react-ufo/load-hold';

import { IneligibleAnalytics } from '../analytics/audience-and-timing-analytics';
import {
	EditionAwarenessContext,
	useEditionAwareness,
	type EditionAwarenessContextType,
} from '../services/edition-awareness-context';
import { useGetExperimentValue } from '../services/experiment-context';
import { isEligibleForAutomationFreeLimitModal } from '../services/is-eligible-for-automation-free-limit-modal';
import { isEligibleForConfluenceBuyReverseTrial } from '../services/is-eligible-for-confluence-buy-reverse-trial';
import { isEligibleForConfluencePremiumPopup } from '../services/is-eligible-for-confluence-premium-popup';
import { isEligibleForConfluenceStandardAddPaymentModal } from '../services/is-eligible-for-confluence-standard-add-payment-modal';
import { isEligibleForEAtoOSBFreeWithPDE } from '../services/is-eligible-for-ea-to-osb-free-with-pde';
import { isEligibleForEAtoOSBNonRTPremiumAndRTPredunning } from '../services/is-eligible-for-ea-to-osb-nonrt-premium-and-rt-predunning';
import { isEligibleForExploreEditionBenefitsModalReverseTrial } from '../services/is-eligible-for-explore-edition-benefits-modal-reverse-trial';
import { isEligibleForExploreEditionBenefitsModalStorageLimit } from '../services/is-eligible-for-explore-edition-benefits-modal-storage-limit';
import { isEligibleForExploreEditionBenefitsModalWithPDE } from '../services/is-eligible-for-explore-edition-benefits-modal-with-pde';
import { isEligibleForExploreEditionBenefitsModalWithoutPDE } from '../services/is-eligible-for-explore-edition-benefits-modal-without-pde';
import { isEligibleForFreeToStandardModal } from '../services/is-eligible-for-free-to-standard-modal';
import { isEligibleForJiraPremiumPredunning } from '../services/is-eligible-for-jira-premium-predunning';
import { isEligibleForJiraStandardPredunning } from '../services/is-eligible-for-jira-standard-predunning';
import {
	isEligibleForJiraStandardStorageHardLimit,
	isJiraStandardStorageNearLimit,
} from '../services/is-eligible-for-jira-standard-storage-hard-limit';
import { isEligibleForJsmPersonalisedStandardToPremium } from '../services/is-eligible-for-jsm-personalised-standard-to-premium';
import { isEligibleForJsmStorageNearLimit } from '../services/is-eligible-for-jsm-storage-near-limit';
import { isEligibleForJsmStorageOverLimit } from '../services/is-eligible-for-jsm-storage-over-limit';
import { isEligibleForPremiumAddPaymentModal } from '../services/is-eligible-for-premium-add-payment-modal';
import { isEligibleForPremiumPaymentDetailsPopup } from '../services/is-eligible-for-premium-payment-details-popup';
import { isEligibleForPremiumToTwc } from '../services/is-eligible-for-premium-to-twc';
import { isEligibleForReverseTrial } from '../services/is-eligible-for-reverse-trial';
import { isEligibleForReverseTrialPlanSelection } from '../services/is-eligible-for-reverse-trial-plan-selection';
import { isEligibleForStandardAddPaymentModal } from '../services/is-eligible-for-standard-add-payment-modal';
import { isEligibleForStandardPaymentDetailsPopup } from '../services/is-eligible-for-standard-payment-details-popup';
import { isEligibleForStandardPaymentFlowLink } from '../services/is-eligible-for-standard-payment-flow-link';
import { isEligibleForStandardToPremium } from '../services/is-eligible-for-standard-to-premium';
import { isEligibleForStandardToPremiumIncentiveOffer } from '../services/is-eligible-for-standard-to-premium-incentive-offer';
import { isEligibleForStorageHardLimit } from '../services/is-eligible-for-storage-hard-limit';
import { isEligibleForTWCAddPaymentDetails } from '../services/is-eligible-for-twc-add-payment-details';
import { isEligibleForTWCChangePlan } from '../services/is-eligible-for-twc-change-plan';
import { isEligibleForTWCPlanSelectionPage } from '../services/is-eligible-for-twc-plan-selection-page';
import { isEligibleForTWCTrialNoPDE } from '../services/is-eligible-for-twc-trial-no-pde';
import { isEligibleForUserLimitDirectBuy } from '../services/is-eligible-for-user-limit-direct-buy';
import { isPaidPremium } from '../services/is-paid-premium';
import { isPaidStandard } from '../services/is-paid-standard';
import { useGetStandardStorageHardLimitData } from '../services/use-get-standard-storage-hard-limit-data';
import { USER_LIMIT_DIRECT_BUY_EXPERIMENT } from '../services/user-limit-direct-buy-experiment';
import { AutomationFreeLimitModal } from './dialogs/automation-free-limit-modal';
import { CollectionAddPaymentDetailsPopup } from './dialogs/collection-add-payment-details-popup';
import { CollectionChangePlanPopup } from './dialogs/collection-change-plan-popup';
import { CollectionTrialModal } from './dialogs/collection-trial-modal';
import { ExploreEditionBenefitsModal } from './dialogs/explore-edition-benefits-modal/explore-edition-benefits-modal';
import { FreeToStandardModal } from './dialogs/free-to-standard-modal';
import { JsmPersonalisedPremiumUpsellModal } from './dialogs/jsm-personalised-premium-upsell-modal';
import { PaidToTwcModal } from './dialogs/paid-to-twc-modal';
import { PaymentDetailsPopup } from './dialogs/payment-details-popup';
import { PlanSelectionPageModal } from './dialogs/plan-selection-page-modal';
import { PremiumAddPaymentModal } from './dialogs/premium-add-payment-modal';
import { PremiumPopup } from './dialogs/premium-popup';
import { ReverseTrialPopupDialog } from './dialogs/reverse-trial-popup';
import { StandardAddPaymentModal } from './dialogs/standard-add-payment-modal';
import { StandardStorageHardLimitModal } from './dialogs/standard-storage-hard-limit-modal';
import { StandardToPremiumIncentiveOfferModal } from './dialogs/standard-to-premium-incentive-offer-modal';
import { StandardToPremiumModal } from './dialogs/standard-to-premium-modal';
import { StorageHardLimitModal } from './dialogs/storage-hard-limit-modal';
import { UserLimitDirectBuyModal } from './dialogs/user-limit-direct-buy-modal';
import { PaymentFlowLink } from './links/payment-flow-link';
import { UpgradeStandardFlowLink } from './links/upgrade-standard-flow-link';

function FreeEdition(): React.JSX.Element {
	const context = useEditionAwareness();
	const { getExperimentCohort } = useGetExperimentValue();

	if (isEligibleForTWCPlanSelectionPage(context)) {
		return <PlanSelectionPageModal type="twc" />;
	}

	if (isEligibleForAutomationFreeLimitModal(context)) {
		return <AutomationFreeLimitModal />;
	}

	if (isEligibleForStorageHardLimit(context)) {
		return <StorageHardLimitModal />;
	}

	// Claim eligible variation traffic before the overlapping PDE upgrade-link route.
	if (isEligibleForUserLimitDirectBuy(context)) {
		const cohort = getExperimentCohort(USER_LIMIT_DIRECT_BUY_EXPERIMENT, false);
		if (cohort === 'variation') {
			return <UserLimitDirectBuyModal />;
		}
	}

	if (isEligibleForEAtoOSBFreeWithPDE(context)) {
		return <UpgradeStandardFlowLink />;
	}

	if (
		isEligibleForJsmStorageOverLimit(context) ||
		isEligibleForJsmStorageNearLimit(context) ||
		isEligibleForFreeToStandardModal(context)
	) {
		return <FreeToStandardModal />;
	}

	return <IneligibleAnalytics />;
}

function StandardEdition(): React.JSX.Element {
	const context = useEditionAwareness();
	const { product, currentRoute } = context;
	const { getExperimentCohort } = useGetExperimentValue();

	if (isEligibleForTWCTrialNoPDE(context)) {
		// Teamwork Collection with no PDE in trial
		return <PlanSelectionPageModal type="twc" />;
	}

	if (isEligibleForTWCChangePlan(context)) {
		if (fg('twc-pde-trial-ea-modal-update-gate')) {
			const cohort = getExperimentCohort('twc-pde-trial-ea-modal-update-experiment', false);

			if (cohort === 'variation') {
				return <CollectionTrialModal />;
			}
		}

		return <CollectionChangePlanPopup />;
	}

	if (isEligibleForTWCAddPaymentDetails(context)) {
		return <CollectionAddPaymentDetailsPopup />;
	}

	if (isEligibleForReverseTrialPlanSelection(context)) {
		return <PlanSelectionPageModal type="reverse-trial" />;
	}

	// Storage-limit auto-upgrade path is independent of the STAR experiment / its
	// killswitch / cohort. Render the modal whenever the dedicated storage-limit
	// killswitch is on and the entitlement trial trigger is AUTO_UPGRADE_STORAGE_LIMIT.
	if (
		isEligibleForExploreEditionBenefitsModalStorageLimit(context) ||
		isEligibleForExploreEditionBenefitsModalReverseTrial(context)
	) {
		return <ExploreEditionBenefitsModal />;
	}

	if (isEligibleForExploreEditionBenefitsModalWithPDE(context)) {
		if (getExperimentCohort('jsmgrowth466_star') === 'variation') {
			return <ExploreEditionBenefitsModal />;
		}
		return <IneligibleAnalytics />;
	}

	if (isEligibleForExploreEditionBenefitsModalWithoutPDE(context)) {
		if (getExperimentCohort('jsmgrowth466_star') === 'variation') {
			return <ExploreEditionBenefitsModal />;
		}
		// jira-service-management intentionally falls through to the downstream isEligibleForStandardAddPaymentModal handler
	}

	// Catch-all: block CSM and ops routes that fell through (e.g. killswitch OFF, not in trial, or non-variation cohort)
	if (product === 'jira-customer-service' || currentRoute === 'ops') {
		return <IneligibleAnalytics />;
	}

	if (isEligibleForStandardPaymentFlowLink(context)) {
		return <PaymentFlowLink />;
	}

	if (isEligibleForStandardAddPaymentModal(context)) {
		return <StandardAddPaymentModal />;
	}

	// Jira Standard - Site admins get routed to JSTO, others to payment popup
	if (isEligibleForStandardPaymentDetailsPopup(context)) {
		return <PaymentDetailsPopup />;
	}

	if (isEligibleForConfluenceStandardAddPaymentModal(context)) {
		return <StandardAddPaymentModal />;
	}

	if (isEligibleForJiraStandardPredunning(context)) {
		return <PaymentFlowLink />;
	}

	return <IneligibleAnalytics />;
}

function PaidStandardUpsells(): React.JSX.Element {
	const context = useEditionAwareness();
	const { getExperimentCohort } = useGetExperimentValue();

	if (isEligibleForStandardToPremiumIncentiveOffer(context)) {
		// The button is unchanged; defer exposure until the selected modal renders.
		const cohort = getExperimentCohort('standard_to_premium_incentive_offer_experiment', false);

		if (cohort === 'variation') {
			return <StandardToPremiumIncentiveOfferModal />;
		}
	}

	if (isEligibleForJsmPersonalisedStandardToPremium(context)) {
		return <JsmPersonalisedPremiumUpsellModal />;
	}

	if (isEligibleForStandardToPremium(context)) {
		return <StandardToPremiumModal />;
	}

	return <IneligibleAnalytics />;
}

function JiraStandardStorageHardLimit(): React.JSX.Element {
	const context = useEditionAwareness();

	// requires fetching the collection membership async from root query to avoid calling TCS on ineligible Standard sites
	const { getExperimentCohort } = useGetExperimentValue();
	const { data, loading, error } = useGetStandardStorageHardLimitData();

	const isAvailableToUser = data?.isAvailableToUser;
	const experienceUrl = data?.experienceUrl;
	const isInCollection = data?.isInCollection;

	const canUpgradeToPremium = isAvailableToUser === true && Boolean(experienceUrl);

	const contextWithCollection = useMemo<EditionAwarenessContextType>(
		() => ({
			...context,
			canUpgradeToPremium,
			...(isInCollection && { isInCollection }),
		}),
		[context, canUpgradeToPremium, isInCollection],
	);

	if (loading) {
		return <UFOLoadHold name="jira-standard-storage-hard-limit-edition-awareness" />;
	}

	if (error) {
		return <></>;
	}

	// Within 1 GB of the allowance only users who can upgrade to Premium are exposed; at or over
	// it everyone not in a collection is. The pill in `getStandardContent` applies the same guard.
	const isNearLimit = isJiraStandardStorageNearLimit(context);

	// if near limit = true, must have canUpgradeToPremium = true as near limit is only shown to users who can upgrade to Premium
	if (isInCollection === 'NOT_IN_COLLECTION' && (!isNearLimit || canUpgradeToPremium === true)) {
		// Exposure fires here, having already met all other eligibility criteria
		const cohort = getExperimentCohort('jira-standard-storage-hard-limits', true);

		if (cohort === 'variation') {
			return (
				<EditionAwarenessContext.Provider value={contextWithCollection}>
					<StandardStorageHardLimitModal />
				</EditionAwarenessContext.Provider>
			);
		}
	}

	return <PaidStandardUpsells />;
}

function PaidStandard(): React.JSX.Element {
	const context = useEditionAwareness();

	if (isEligibleForJiraStandardStorageHardLimit(context)) {
		return <JiraStandardStorageHardLimit />;
	}

	return <PaidStandardUpsells />;
}

function PaidPremium(): React.JSX.Element {
	const context = useEditionAwareness();
	const { getExperimentCohort } = useGetExperimentValue();

	if (isEligibleForPremiumToTwc(context)) {
		getExperimentCohort('premium-to-twc-upsell-aa', true);

		const cohort = getExperimentCohort('premium-to-twc-upsell-ab');

		if (cohort === 'variation') {
			return <PaidToTwcModal />;
		}
	}

	return <IneligibleAnalytics />;
}

function PremiumEdition(): React.JSX.Element {
	const context = useEditionAwareness();
	const { getExperimentCohort } = useGetExperimentValue();
	// make sure twc is always shown first before jira reverse trial
	// otherwise, jira reverse trial will be shown with collection

	// additional check on invoiceableStatus and canAddPaymentDetails
	// to block any EA to be shown if the customer is in a collection
	// * confluence premium is showing EA regardless which needs to be blocked from collection
	if (isEligibleForTWCTrialNoPDE(context)) {
		// Teamwork Collection with no PDE in trial
		return <PlanSelectionPageModal type="twc" />;
	}

	if (isEligibleForTWCChangePlan(context)) {
		if (fg('twc-pde-trial-ea-modal-update-gate')) {
			const cohort = getExperimentCohort('twc-pde-trial-ea-modal-update-experiment', false);

			if (cohort === 'variation') {
				return <CollectionTrialModal />;
			}
		}

		return <CollectionChangePlanPopup />;
	}

	if (isEligibleForTWCAddPaymentDetails(context)) {
		return <CollectionAddPaymentDetailsPopup />;
	}

	if (isEligibleForEAtoOSBNonRTPremiumAndRTPredunning(context)) {
		const cohort = getExperimentCohort('ea-to-osb-nonrt-premium-rt-predunning-ab', false);

		if (cohort === 'variation') {
			return <PaymentFlowLink />;
		}
	}

	if (isEligibleForReverseTrialPlanSelection(context)) {
		return <PlanSelectionPageModal type="reverse-trial" />;
	}

	if (isEligibleForReverseTrial(context)) {
		return <ReverseTrialPopupDialog />;
	}

	if (isEligibleForPremiumPaymentDetailsPopup(context)) {
		return <PaymentDetailsPopup />;
	}

	if (isEligibleForJiraPremiumPredunning(context)) {
		return <PaymentFlowLink />;
	}

	if (isEligibleForConfluenceBuyReverseTrial(context)) {
		return <PlanSelectionPageModal type="reverse-trial" />;
	}

	if (isEligibleForConfluencePremiumPopup(context)) {
		return <PremiumPopup />;
	}

	if (isEligibleForPremiumAddPaymentModal(context)) {
		return <PremiumAddPaymentModal />;
	}

	return <IneligibleAnalytics />;
}

/**
 * Covers what button and action we show to the customer.
 *
 * Experiments here should just modify what is shown to the customer not if it is shown to the customer.
 */
export function ActionsAndContent(): React.JSX.Element | null {
	const context = useEditionAwareness();
	const { edition } = context;

	if (edition === 'free') {
		return <FreeEdition />;
	}

	if (isPaidStandard(context)) {
		return <PaidStandard />;
	}

	if (edition === 'standard') {
		return <StandardEdition />;
	}

	if (isPaidPremium(context)) {
		return <PaidPremium />;
	}

	if (edition === 'premium') {
		return <PremiumEdition />;
	}

	return <IneligibleAnalytics />;
}
